import { useRouter } from "next/router";
import type { FunctionComponent } from "react";
import { useEffect } from "react";
import { graphql, useQueryLoader } from "react-relay";

import { ClientSideSuspense } from "shared/components/common/client-side-suspense";
import { notEmpty } from "shared/lib/utils";

import { DefaultPageSize } from "scmp-app/components/search/consts";
import { useSearchContext } from "scmp-app/components/search/contexts";
import type { searchResultPanelQuery } from "scmp-app/queries/__generated__/searchResultPanelQuery.graphql";

import { SearchResultList } from "./search-result-list";
import { StyledLoading } from "./styles";

export const query = graphql`
  query searchResultPanelQuery(
    $query: String!
    $articleTypeIds: [String!]
    $sectionIds: [String!]
    $contentTypes: [ContentTypeEnum!]
    $paywallTypeIds: [String!]
    $orderBy: ArticlesSearchOrderByInput
    $first: Int = 10
    $after: String
    $startDate: Timestamp
    $endDate: Timestamp
  ) {
    ...searchResultListArticleSearchQuery
    ...helpersSectionMenuQuery
    ...helpersTypeMenuQuery
    topicSearch(first: 10, filter: { query: $query }) {
      ...searchResultListTopicSearchConnection
    }
    sectionSearch(first: 10, filter: { query: $query }) {
      ...searchResultListSectionSearchConnection
    }
  }
`;

export const SearchResultPanel: FunctionComponent = () => {
  const { events: routerEvents, query: routerQuery } = useRouter();
  const {
    articleTypeIds,
    contentTypes,
    currentValue,
    dateRange,
    orderBy,
    paywallTypeIds,
    sectionIds,
  } = useSearchContext();
  const [queryReference, loadQuery, disposeQuery] = useQueryLoader<searchResultPanelQuery>(query);

  useEffect(() => {
    const handler = () => {
      if (!notEmpty(currentValue)) return;
      if (routerQuery.q === currentValue) return;

      loadQuery(
        {
          articleTypeIds,
          contentTypes,
          first: DefaultPageSize,
          orderBy,
          paywallTypeIds,
          query: currentValue,
          sectionIds,
          ...dateRange,
        },
        { fetchPolicy: "network-only" },
      );
    };
    routerEvents.on("routeChangeComplete", handler);
    return () => {
      routerEvents.off("routeChangeComplete", handler);
      disposeQuery();
    };
  }, [
    articleTypeIds,
    contentTypes,
    currentValue,
    dateRange,
    disposeQuery,
    loadQuery,
    orderBy,
    paywallTypeIds,
    routerEvents,
    routerQuery.q,
    sectionIds,
  ]);

  useEffect(() => {
    if (!notEmpty(currentValue)) return;
    if (routerQuery.q !== currentValue) return;
    loadQuery(
      {
        articleTypeIds,
        contentTypes,
        first: DefaultPageSize,
        orderBy,
        paywallTypeIds,
        query: currentValue,
        sectionIds,
        ...dateRange,
      },
      { fetchPolicy: "network-only" },
    );
    return () => {
      disposeQuery();
    };
  }, [
    articleTypeIds,
    contentTypes,
    currentValue,
    dateRange,
    disposeQuery,
    loadQuery,
    orderBy,
    paywallTypeIds,
    routerQuery.q,
    sectionIds,
  ]);

  if (!notEmpty(currentValue)) return null;

  return (
    <ClientSideSuspense fallback={<StyledLoading />}>
      {queryReference && <SearchResultList queryReference={queryReference} />}
    </ClientSideSuspense>
  );
};

SearchResultPanel.displayName = "SearchResultPanel";
